import { api } from './api';
import { useAuthStore } from '@/stores/auth';
import type { UserLanguage, UserRole } from '@xtreed/academy-shared';

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string | null;
  cpf?: string | null;
  phone?: string | null;
  birthDate?: Date | null;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  language: UserLanguage;
  createdAt: Date;
}

export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
}

export interface TwoFactorSetupResponse {
  secret: string;
  qrCode: string;
}

export const authService = {
  async login(data: LoginData): Promise<AuthResponse> {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  async logout(): Promise<void> {
    const { refreshToken } = useAuthStore.getState();
    await api.post('/auth/logout', { refreshToken });
  },

  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const response = await api.post('/auth/refresh', { refreshToken });
    return response.data;
  },

  async getProfile(): Promise<User> {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  async updateProfile(data: Record<string, unknown>): Promise<User> {
    const response = await api.patch('/auth/profile', data);
    return response.data;
  },

  async updateLanguage(language: UserLanguage): Promise<void> {
    await api.put('/users/profile', { language });
  },

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    await api.post('/auth/change-password', { currentPassword, newPassword });
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email });
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await api.post('/auth/reset-password', { token, password });
  },

  async verifyEmail(token: string): Promise<void> {
    await api.post('/auth/verify-email', { token });
  },

  async resendVerificationEmail(): Promise<void> {
    await api.post('/auth/resend-verification');
  },

  // 2FA
  async setup2FA(): Promise<TwoFactorSetupResponse> {
    const response = await api.post('/auth/2fa/setup');
    return response.data;
  },

  async enable2FA(code: string): Promise<{ backupCodes: string[] }> {
    const response = await api.post('/auth/2fa/enable', { code });
    return response.data;
  },

  async disable2FA(code: string): Promise<void> {
    await api.post('/auth/2fa/disable', { code });
  },

  async verify2FA(code: string): Promise<AuthResponse> {
    const response = await api.post('/auth/2fa/verify', { code });
    return response.data;
  },

  // MFA
  async mfaInit(email: string) {
    const { data } = await api.post('/auth/mfa/init', { email });
    return data?.data || data;
  },

  async mfaSendCode(sessionId: string, method: string) {
    const { data } = await api.post('/auth/mfa/send-code', {
      sessionId,
      method,
    });
    return data?.data || data;
  },

  async mfaVerify(sessionId: string, method: string, code: string) {
    const { data } = await api.post('/auth/mfa/verify', {
      sessionId,
      method,
      code,
    });
    return data?.data || data;
  },

  // OAuth
  async googleAuth(): Promise<{ url: string }> {
    const response = await api.get('/auth/google');
    return response.data;
  },

  async googleCallback(code: string): Promise<AuthResponse> {
    const response = await api.get(`/auth/google/callback?code=${code}`);
    return response.data;
  },
};
